import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";

// Admin sign-in: Google OAuth only. On success the browser returns to the SPA
// origin root, where the client's detectSessionInUrl completes the OAuth/PKCE
// code exchange and the route guard (RequireAdmin) lets verified admins through.
// The real authorization boundary is the edge functions' is_admin check — signing
// in only establishes identity; a non-admin Google account gets a session but no
// admin API access.
export default function Login() {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();

  // Why the user landed here (ADM-1 / ADM-15):
  //   ?reason=expired          — the interceptor signed them out on a 401.
  //   ?error / error_description — an OAuth provider bounced the callback
  //                                (e.g. access_denied); previously swallowed.
  const reason = params.get("reason");
  const oauthError =
    params.get("error_description") ?? params.get("error") ?? null;
  const notice = oauthError
    ? oauthError.replace(/\+/g, " ")
    : reason === "expired"
      ? "Your session expired. Please sign in again."
      : null;

  async function handleGoogle() {
    setLoading(true);
    setError(null);
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/` },
    });
    // On success the browser navigates to Google; only an error returns here.
    if (oauthError) {
      setError(oauthError.message);
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-900">
      <div className="w-full max-w-sm space-y-6 px-4">
        <div>
          <div className="text-lg font-semibold text-zinc-100">Surround Admin</div>
          <div className="text-sm text-zinc-500">Sign in to continue</div>
        </div>

        {notice && !error && (
          <div className="rounded border border-amber-900 bg-amber-950 px-3 py-2 text-sm text-amber-300">
            {notice}
          </div>
        )}

        {error && <div className="text-sm text-red-400">{error}</div>}

        <button
          type="button"
          onClick={handleGoogle}
          disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded bg-zinc-100 px-3 py-2 text-sm font-medium text-zinc-900 hover:bg-white disabled:opacity-50"
        >
          <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
            <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
          </svg>
          {loading ? "Signing in…" : "Continue with Google"}
        </button>
      </div>
    </div>
  );
}
